import React, { useCallback, useEffect } from 'react';

import { Box, Tooltip } from '@mui/material';

import { isElementWithGeneratedChildren } from '@shared/helpers/element.helpers';
import { IElement } from '@shared/models/project.model';

import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import {
  closeMenu,
  menuMouseHandler,
  openMenu as openContextMenu,
  useMenuPosition,
} from '@client/hooks/use-menu.hooks';
import { Menu } from '@ui/menus/Menu';

import amplitudeLog from '@client/amplitude';
import {
  AMP_ELEMENT_DELETE,
  AMP_ELEMENT_DISABLE,
  AMP_ELEMENT_DUPLICATE,
} from '@shared/constants';
import { useIsReadonlyElement } from '@client/hooks/use-user.hooks';
import {
  useAddStandardElement,
  useDuplicateElement,
  useRemoveElements,
  useApplyProjectChanges,
} from '@client/store/project';
import { useSnackbarWithUndo } from '@client/hooks/use-undo-snackbar.hooks';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import type { IElementID } from '@shared/models/project.model';
import {
  consumePendingRestoreElementId,
  setPendingRestoreElementId,
} from '@client/hooks/use-undo-element-restore';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import { useGroupedElementCategories } from '@features/elements/hooks/use-grouped-element-categories.hook';
import { Icon } from '@ui/Icon';
import KebabButton from '@ui/menus/KebabMenu/components/KebabButton';
import { KebabMenuProps } from '@ui/menus/KebabMenu/kebabmenu.model';
import { ElementCategoryMenuContent } from '@features/elements/components/ElementCategoryMenu/ElementCategoryMenuContent';
import { mergeSx } from '@client/helpers/mui.helpers';

const kebabMenuLayoutSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  m: '6px 8px',
  width: 20,
  transition: 'opacity 0.3s',
} as const;

type IElementKebabMenuInput = Pick<
  KebabMenuProps,
  | 'anchor'
  | 'onOpen'
  | 'hidden'
  | 'tooltip'
  | 'buttonProps'
  | 'className'
  | 'sx'
  | 'onClose'
> & {
  element: IElement;
  /**
   * When set (e.g. proposal-resolved element in the element list), {@link IElement.isDeactivated}
   * for the Enable/Disable item and toggle logic follows this object. Baseline {@link element} is
   * still used for tree path hooks and mutations so flat-tree identity stays correct.
   */
  resolvedElement?: IElement;
  onRename?: () => void;
};

export const ElementKebabMenu: React.FC<IElementKebabMenuInput> = ({
  element,
  anchor,
  onOpen,
  hidden,
  tooltip,
  buttonProps,
  className,
  sx,
  onClose,
  onRename,
  resolvedElement = element,
}) => {
  const addElement = useAddStandardElement();
  const duplicateElement = useDuplicateElement();
  const enqueueSnackbarWithUndo = useSnackbarWithUndo();
  const navigateTo = useProjectNavigation();
  const selectedProposalId = useSelectedProposalId();
  const removeElements = useRemoveElements({
    onDeleted: useCallback(
      (name: string, elementId: IElementID) => {
        setPendingRestoreElementId(elementId);
        const message = selectedProposalId
          ? `${name} removed`
          : `${name} deleted`;
        enqueueSnackbarWithUndo(message, {
          withUndo: true,
          autoHideDuration: 8000,
          beforeUndo: () => consumePendingRestoreElementId(),
          afterUndo: (idToRestore) => {
            if (typeof idToRestore === 'string' && idToRestore)
              navigateTo({ elementId: idToRestore });
          },
        });
      },
      [selectedProposalId, enqueueSnackbarWithUndo, navigateTo],
    ),
  });
  const applyChanges = useApplyProjectChanges();

  const readonly = useIsReadonlyElement(element);
  const isProductElement = isElementWithGeneratedChildren(element);
  const isDeactivated = resolvedElement.isDeactivated;
  const categoryItems = useGroupedElementCategories();
  const menuPosition = useMenuPosition(element.id);

  if (!element.id) {
    throw new Error('Element ID is required');
  }

  const handleRemoveElement = useCallback(async () => {
    await removeElements(element.id);
    amplitudeLog(AMP_ELEMENT_DELETE, { ElementID: element.id });
  }, [element.id, removeElements]);

  const handleToggleVisibility = useCallback(async () => {
    await applyChanges([
      {
        type: !isDeactivated
          ? ProjectChangeType.ElementDeactivate
          : ProjectChangeType.ElementActivate,
        elementId: element.id,
      },
    ]);
    amplitudeLog(AMP_ELEMENT_DISABLE, {
      ElementID: element.id,
      disabled: !isDeactivated,
    });
  }, [applyChanges, isDeactivated, element.id]);

  const handleDuplicateClick = useCallback(() => {
    duplicateElement(element);
    amplitudeLog(AMP_ELEMENT_DUPLICATE, { ElementID: element.id });
  }, [duplicateElement, element]);

  const isMenuOpen = Boolean(anchor || menuPosition);

  const handleMenuClose = useCallback(() => {
    closeMenu();
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleGlobalContextMenu = () => closeMenu();
    document.addEventListener('contextmenu', handleGlobalContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleGlobalContextMenu);
    };
  }, [isMenuOpen]);

  const handleButtonClick = useMouseEventCallback(
    (event) => {
      const anchorPosition = menuMouseHandler(event);
      if (anchorPosition) {
        openContextMenu({ anchorPosition, id: element.id });
        onOpen?.(event.currentTarget);
      }
    },
    { stopPropagation: true },
  );

  if (readonly) return null;

  return (
    <Box
      className={className}
      sx={mergeSx(kebabMenuLayoutSx, { opacity: hidden ? 0 : 1 }, sx)}
    >
      <Tooltip disableInteractive title={tooltip ?? 'Element options'}>
        <Box>
          <KebabButton
            {...buttonProps}
            disabled={hidden}
            hidden={hidden}
            onClick={handleButtonClick}
          />
        </Box>
      </Tooltip>

      <Menu
        open={isMenuOpen}
        anchorEl={anchor}
        anchorPosition={menuPosition}
        onClose={handleMenuClose}
      >
        {!isProductElement && categoryItems.length > 0 && (
          <Menu.Submenu
            label="Add Element"
            icon={<Icon category="mui" icon="Add" />}
          >
            <ElementCategoryMenuContent
              categoryGroups={categoryItems}
              onSelect={(categoryId) => addElement(element, categoryId)}
            />
          </Menu.Submenu>
        )}

        {onRename && (
          <Menu.Item
            icon={<Icon category="mui" icon="DriveFileRenameOutline" />}
            disabled={isDeactivated ? 'Enable the element to rename it' : false}
            onClick={() => {
              handleMenuClose();
              onRename();
            }}
          >
            Rename
          </Menu.Item>
        )}

        <Menu.Item
          icon={
            <Icon
              category="mui"
              icon={isDeactivated ? 'Visibility' : 'VisibilityOff'}
            />
          }
          onClick={handleToggleVisibility}
        >
          {isDeactivated ? 'Enable' : 'Disable'}
        </Menu.Item>

        <Menu.Item
          icon={<Icon category="mui" icon="ContentCopy" />}
          onClick={handleDuplicateClick}
        >
          Duplicate
        </Menu.Item>

        <Menu.Item
          icon={<Icon category="mui" icon="DeleteForever" />}
          onClick={handleRemoveElement}
        >
          Delete
        </Menu.Item>
      </Menu>
    </Box>
  );
};
